import React from 'react';

interface ServiceNowKnownFieldProps {
  pulseNumber?: string | null;
  serviceNowNumber?: string | null;
  serviceNowKnown?: boolean;
  serviceNowLinked?: boolean;
  createdFromServiceNow?: boolean;
  syncedToServiceNow?: boolean;
  /** Inline badge for tight header rows */
  compact?: boolean;
  /** Inline with header badges (same row, right side) */
  placement?: 'form' | 'header';
}

interface LinkDisplay {
  tag: string;
  referenceText: string;
  originLabel: string;
}

function getLinkDisplay(
  createdFromServiceNow?: boolean,
  syncedToServiceNow?: boolean,
  pulseNumber?: string | null,
  serviceNowNumber?: string | null
): LinkDisplay {
  const pulse = normalizeTicketNumber(pulseNumber);
  const serviceNow = normalizeTicketNumber(serviceNowNumber);

  if (createdFromServiceNow) {
    return {
      tag: 'ServiceNow',
      referenceText: [serviceNow, pulse].filter(Boolean).join(' ↔ '),
      originLabel: 'Created in ServiceNow, linked to Pulse',
    };
  }
  if (syncedToServiceNow) {
    return {
      tag: 'Pulse',
      referenceText: [pulse, serviceNow].filter(Boolean).join(' ↔ '),
      originLabel: 'Created in Pulse, linked to ServiceNow',
    };
  }
  return {
    tag: 'ServiceNow',
    referenceText: [pulse, serviceNow].filter(Boolean).join(' ↔ '),
    originLabel: 'Linked with ServiceNow',
  };
}

function normalizeTicketNumber(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '[object Object]') return null;
    return trimmed;
  }
  if (typeof value === 'object') {
    const record = value as { value?: unknown; display_value?: unknown };
    const fromValue = record.value != null ? String(record.value).trim() : '';
    const fromDisplay =
      record.display_value != null ? String(record.display_value).trim() : '';
    const resolved = fromDisplay || fromValue;
    if (!resolved || resolved === '[object Object]') return null;
    return resolved;
  }
  const asText = String(value).trim();
  if (!asText || asText === '[object Object]') return null;
  return asText;
}

function LinkCard({
  tag,
  referenceText,
  originLabel,
  className = '',
}: LinkDisplay & { className?: string }) {
  return (
    <div
      className={`px-3 py-2 border border-indigo-200 rounded bg-indigo-50 text-indigo-900 text-sm min-h-8 flex flex-col gap-1 ${className}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-800">
          {tag}
        </span>
        {referenceText ? (
          <span className="font-medium whitespace-nowrap">{referenceText}</span>
        ) : (
          <span className="font-medium">Link established</span>
        )}
      </div>
      <span className="text-xs text-indigo-700">{originLabel}</span>
    </div>
  );
}

export function ServiceNowKnownField({
  pulseNumber,
  serviceNowNumber,
  serviceNowKnown,
  serviceNowLinked,
  createdFromServiceNow,
  syncedToServiceNow,
  compact = false,
  placement = 'form',
}: ServiceNowKnownFieldProps) {
  if (!serviceNowKnown && !serviceNowLinked) {
    return null;
  }

  const linkDisplay = getLinkDisplay(
    createdFromServiceNow,
    syncedToServiceNow,
    pulseNumber,
    serviceNowNumber
  );

  if (compact) {
    return (
      <LinkCard
        {...linkDisplay}
        className="inline-flex flex-shrink-0"
      />
    );
  }

  if (placement === 'header') {
    return (
      <LinkCard
        {...linkDisplay}
        className="flex-shrink-0"
      />
    );
  }

  return (
    <LinkCard
      {...linkDisplay}
      className="w-full"
    />
  );
}

export default ServiceNowKnownField;
